import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCcw, TrendingUp, Clock, Users, Award, Activity, Percent,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const PERIODS = [
  { v: "30d", l: "30 dias" },
  { v: "90d", l: "90 dias" },
  { v: "year", l: "Ano atual" },
  { v: "all", l: "Tudo" },
  { v: "custom", l: "Personalizado" },
];

function MetricCard({ label, value, hint, accent = "gold", testId }) {
  const color = {
    gold: "text-[#D4AF37] border-[#D4AF37]/30",
    amber: "text-amber-300 border-amber-500/30",
    cyan: "text-cyan-300 border-cyan-500/30",
    emerald: "text-emerald-300 border-emerald-500/30",
    red: "text-red-300 border-red-500/30",
    neutral: "text-[#A3A3A3] border-[#1f1f1f]",
  }[accent] || "text-[#D4AF37] border-[#D4AF37]/30";
  return (
    <div className={`rounded-md border ${color} bg-[#0a0a0a] px-3 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-lg mt-1 ${color.split(" ")[0]}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-md px-3 py-2 text-[11px]">
      <div className="text-[#D4AF37] font-medium mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span style={{ background: p.color }} className="inline-block w-2 h-2 rounded-sm"/>
          <span className="text-[#A3A3A3]">{p.name}:</span>
          <span className="font-mono-tab" style={{ color: p.color }}>
            {p.dataKey.startsWith("valor") ? formatBRL(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function RecuperacaoPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("90d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (customStart) params.start_date = customStart;
        if (customEnd) params.end_date = customEnd;
      }
      const { data } = await api.get("/recovery/performance", { params });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar performance");
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { if (period !== "custom") load(); }, [period, load]);

  const k = data?.kpis || {};
  const series = data?.monthly_series || [];

  // Chart data — normalize month label
  const chartData = useMemo(() => series.map((m) => {
    const [y, mo] = m.month.split("-");
    const monthLabel = `${["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][parseInt(mo,10)-1]}/${y.slice(2)}`;
    return { ...m, monthLabel };
  }), [series]);

  return (
    <div data-testid="recuperacao-performance-section">
      {/* Filtros de período */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                period === p.v
                  ? "border-[#D4AF37]/50 text-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-[#1f1f1f] text-[#A3A3A3] hover:bg-[#0f0f0f]"
              }`}
              data-testid={`period-${p.v}`}
            >
              {p.l}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex gap-1.5 items-center">
            <input type="date" className="fc-input text-[12px]" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start"/>
            <span className="text-[#737373] text-[12px]">até</span>
            <input type="date" className="fc-input text-[12px]" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end"/>
            <button onClick={load} className="fc-btn-ghost text-xs" data-testid="custom-apply">Aplicar</button>
          </div>
        )}
        <button onClick={load} disabled={loading} className="fc-btn-ghost text-xs flex items-center gap-1.5 sm:ml-auto" data-testid="btn-perf-reload">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
          Atualizar
        </button>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <MetricCard label="Tempo médio até recuperação" value={`${k.tempo_medio_recuperacao_dias} d`} accent="cyan" hint="Apenas recuperados" testId="kpi-tempo-medio"/>
            <MetricCard label="Taxa de recuperação" value={`${k.taxa_recuperacao_pct}%`} accent="emerald" hint="Recuperados no período ÷ entradas no período" testId="kpi-taxa"/>
            <MetricCard label="Ticket médio recuperados" value={formatBRL(k.ticket_medio_recuperados)} accent="emerald" hint="Valor pago ÷ contratos" testId="kpi-ticket"/>
            <MetricCard label="Contratos no período" value={`${k.total_entradas} → ${k.total_recuperados}`} accent="amber" hint={`${k.total_entradas} entradas · ${k.total_recuperados} recuperados`} testId="kpi-contratos"/>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <MetricCard label="Valor enviado p/ recuperação no período" value={formatBRL(k.total_valor_enviado)} accent="amber" testId="kpi-valor-enviado"/>
            <MetricCard label="Valor efetivamente recuperado no período" value={formatBRL(k.total_valor_recuperado)} accent="emerald" testId="kpi-valor-recuperado"/>
          </div>

          {/* Chart mensal */}
          <div className="fc-card p-5 mb-6" data-testid="performance-chart-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-[15px] font-semibold flex items-center gap-2">
                <Activity size={14} className="text-cyan-300"/> Evolução mensal da carteira
              </h3>
              <div className="text-[10.5px] text-[#737373]">{data.period.start} → {data.period.end}</div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3"/>
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: "#737373" }} axisLine={{ stroke: "#1f1f1f" }}/>
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#737373" }} axisLine={{ stroke: "#1f1f1f" }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#737373" }} axisLine={{ stroke: "#1f1f1f" }}/>
                <Tooltip content={<ChartTooltip/>}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Bar yAxisId="left" dataKey="valor_enviado" name="Enviado" fill="#f59e0b" radius={[4,4,0,0]}/>
                <Bar yAxisId="left" dataKey="valor_recuperado" name="Recuperado" fill="#10b981" radius={[4,4,0,0]}/>
                <Line yAxisId="right" type="monotone" dataKey="entradas" name="Entradas" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }}/>
                <Line yAxisId="right" type="monotone" dataKey="recuperados" name="Recuperações" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Rankings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="fc-card p-5" data-testid="ranking-tempo-card">
              <h3 className="font-display text-[14px] font-semibold mb-3 flex items-center gap-2">
                <Clock size={13} className="text-amber-300"/> Top 10 — Maior tempo em recuperação
              </h3>
              {data.ranking_tempo.length === 0 ? (
                <div className="text-[12px] text-[#737373] italic">Sem dados.</div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                      <th className="text-left py-1.5 px-1.5">Cliente</th>
                      <th className="text-right py-1.5 px-1.5">Dias</th>
                      <th className="text-right py-1.5 px-1.5">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ranking_tempo.map((r, idx) => (
                      <tr key={`${r.client_id}-${idx}`} className="border-b border-[#141414]">
                        <td className="py-1.5 px-1.5">{r.client_name}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono-tab text-amber-300">{r.dias_em_recuperacao} d</td>
                        <td className="py-1.5 px-1.5 text-right font-mono-tab">{formatBRL(r.saldo_em_aberto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="fc-card p-5" data-testid="ranking-valor-card">
              <h3 className="font-display text-[14px] font-semibold mb-3 flex items-center gap-2">
                <Award size={13} className="text-fuchsia-300"/> Top 10 — Maiores valores em recuperação
              </h3>
              {data.ranking_valor.length === 0 ? (
                <div className="text-[12px] text-[#737373] italic">Sem dados.</div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                      <th className="text-left py-1.5 px-1.5">Cliente</th>
                      <th className="text-right py-1.5 px-1.5">Saldo</th>
                      <th className="text-right py-1.5 px-1.5">Dias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ranking_valor.map((r, idx) => (
                      <tr key={`${r.client_id}-v-${idx}`} className="border-b border-[#141414]">
                        <td className="py-1.5 px-1.5">{r.client_name}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono-tab text-fuchsia-200">{formatBRL(r.saldo_em_aberto)}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono-tab text-[#A3A3A3]">{r.dias_em_recuperacao} d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Breakdowns: Por Sócio / Indicador / Gestor */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <BreakdownCard title="Por Sócio" rows={data.por_socio} icon={<Users size={13}/>} testId="breakdown-socio"/>
            <BreakdownCard title="Por Indicador" rows={data.por_indicador} icon={<TrendingUp size={13}/>} testId="breakdown-indicador"/>
            <BreakdownCard title="Por Gestor" rows={data.por_gestor} icon={<Percent size={13}/>} testId="breakdown-gestor" emptyMsg="Campo gestor não cadastrado nos contratos"/>
          </div>
        </>
      )}
    </div>
  );
}

function BreakdownCard({ title, rows, icon, testId, emptyMsg }) {
  return (
    <div className="fc-card p-5" data-testid={testId}>
      <h3 className="font-display text-[14px] font-semibold mb-3 flex items-center gap-2">
        {icon} {title}
      </h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-[12px] text-[#737373] italic">{emptyMsg || "Sem dados."}</div>
      ) : (
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
              <th className="text-left py-1 px-1.5">Nome</th>
              <th className="text-right py-1 px-1.5">Ativos</th>
              <th className="text-right py-1 px-1.5">Rec.</th>
              <th className="text-right py-1 px-1.5">Enviado</th>
              <th className="text-right py-1 px-1.5">Taxa</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r, idx) => (
              <tr key={`${r.id || r.name}-${idx}`} className="border-b border-[#141414]">
                <td className="py-1 px-1.5">{r.name}</td>
                <td className="py-1 px-1.5 text-right font-mono-tab text-amber-300">{r.ativos}</td>
                <td className="py-1 px-1.5 text-right font-mono-tab text-emerald-300">{r.recuperados}</td>
                <td className="py-1 px-1.5 text-right font-mono-tab">{formatBRL(r.valor_enviado)}</td>
                <td className="py-1 px-1.5 text-right font-mono-tab text-cyan-300">{r.taxa_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
